import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, Run, Job } from '../api/client'
import { fmtDateTime } from '../utils/datetime'
import { theme } from '../theme'
import { OutboundInfoButton } from '../components/OutboundInfoButton'
import { SlaCell } from '../components/SlaCell'
import { QualityCell } from '../components/QualityCell'
import { useIsAdmin } from '../auth/AuthContext'

export default function RunHistory() {
  const isAdmin = useIsAdmin()
  const { id } = useParams<{ id: string }>()
  const [job, setJob] = useState<Job | null>(null)
  const [runs, setRuns] = useState<Run[]>([])
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkNote, setBulkNote] = useState('')
  const [showBulk, setShowBulk] = useState(false)

  useEffect(() => {
    if (id) {
      api.getJob(id).then(setJob)
      api.listRuns(id, { status: statusFilter || undefined, page }).then(setRuns)
    }
  }, [id, page, statusFilter])

  const reload = () => {
    if (id) api.listRuns(id, { status: statusFilter || undefined, page }).then(setRuns)
  }

  const handleDelete = async (runId: string) => {
    if (!window.confirm('Delete this run? This removes the run record and any stored received image.')) return
    try {
      await api.deleteRun(runId)
      reload()
    } catch (err: any) {
      window.alert(err.body?.detail || err.message || 'Delete failed')
    }
  }

  // The Info affordance is `components/OutboundInfoButton`, shared with the dashboard, the
  // global run history and the batch report. This page used to keep its own copy: a Check
  // Status button gated on `Boolean(run.efax_fax_id)` — the provider-AGNOSTIC outbound handle,
  // so every SFax and SRFax row passed that test — feeding a hand-written Fax Metadata panel
  // whose heading read "eFax fax_id" whatever had actually carried the fax. Both halves of the
  // original defect, still live on this one page after three other surfaces were fixed.
  //
  // The shared button passes the row id and NOTHING else: the endpoint resolves the leg's
  // provider and returns the label its own brand uses, so no caller can reintroduce either.

  // Auto-refresh
  useEffect(() => {
    if (!id) return
    const interval = setInterval(() => {
      api.listRuns(id, { status: statusFilter || undefined, page }).then(setRuns)
    }, 5000)
    return () => clearInterval(interval)
  }, [id, page, statusFilter])

  const handleBulkResolve = async () => {
    if (selected.size === 0 || !bulkNote) return
    await api.bulkResolve(Array.from(selected), bulkNote)
    setSelected(new Set())
    setBulkNote('')
    setShowBulk(false)
    if (id) api.listRuns(id, { status: statusFilter || undefined, page }).then(setRuns)
  }

  const toggleSelect = (runId: string) => {
    const next = new Set(selected)
    next.has(runId) ? next.delete(runId) : next.add(runId)
    setSelected(next)
  }

  const statusColor = (status: string) => {
    const colors: Record<string, string> = {
      e2e_success: '#4caf50', e2e_success_degraded: '#9ccc65',
      quality_checked: '#66bb6a', sent_pending_reception: '#26c6da',
      in_flight: '#42a5f5', sending: '#ffa726',
      scheduled: '#b0bec5', held: '#bdbdbd', send_failed: '#ef5350',
      manually_failed: '#a1887f', receive_unreadable: '#ff7043',
    }
    return colors[status] || '#9aa0a6'
  }

  const statusLabel = (status: string) => {
    const labels: Record<string, string> = {
      e2e_success: 'E2E Success',
      e2e_success_degraded: 'E2E Success (Degraded)',
      quality_checked: 'Completed',
      sent_pending_reception: 'Sent (Pending Reception)',
      in_flight: 'In-Flight',
      sending: 'Sending',
      scheduled: 'Scheduled',
      held: 'Held',
      send_failed: 'Send Failed',
      manually_failed: 'Manually Failed',
      receive_unreadable: 'Receive Unreadable',
    }
    return labels[status] || status
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Runs: {job?.name || '...'}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '0.4rem' }}>
            <option value="">All statuses</option>
            <option value="e2e_success">E2E Success</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_flight">In-Flight</option>
            <option value="quality_checked">Completed</option>
            <option value="send_failed">Send Failed</option>
            <option value="manually_failed">Manually Failed</option>
          </select>
          {selected.size > 0 && isAdmin && (
            <button onClick={() => setShowBulk(true)} style={{ padding: '0.4rem 0.8rem', background: '#f44336', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Mark {selected.size} Failed
            </button>
          )}
        </div>
      </div>

      {showBulk && (
        <div style={{ background: '#3a2e18', padding: '1rem', borderRadius: '4px', marginBottom: '1rem', border: '1px solid #5a4a24' }}>
          <input value={bulkNote} onChange={e => setBulkNote(e.target.value)} placeholder="Note (required)" style={{ width: '60%', padding: '0.4rem', marginRight: '0.5rem', background: '#1e1e1e', color: '#f5f5f5', border: '1px solid #333', borderRadius: '4px' }} />
          <button onClick={handleBulkResolve} disabled={!bulkNote} style={{ padding: '0.4rem 1rem', background: '#f44336', color: '#fff', border: 'none', borderRadius: '4px' }}>Confirm</button>
          <button onClick={() => setShowBulk(false)} style={{ marginLeft: '0.5rem', padding: '0.4rem 1rem', border: '1px solid #555', background: '#1e1e1e', color: '#f5f5f5', borderRadius: '4px' }}>Cancel</button>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#1e1e1e', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
        <thead>
          <tr style={{ background: '#262626', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem' }}><input type="checkbox" onChange={e => setSelected(e.target.checked ? new Set(runs.filter(r => ['in_flight', 'held'].includes(r.status)).map(r => r.id)) : new Set())} /></th>
            <th style={thStyle}>E2E Run ID</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Submitted</th>
            <th style={thStyle}>Combined E2E Time</th>
            <th style={thStyle}>Quality</th>
            <th style={thStyle}>SLA</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(run => (
            <tr key={run.id} style={{ borderTop: '1px solid #333' }}>
              <td style={{ padding: '0.5rem' }}>
                {['in_flight', 'held', 'sending'].includes(run.status) && isAdmin && (
                  <input type="checkbox" checked={selected.has(run.id)} onChange={() => toggleSelect(run.id)} />
                )}
              </td>
              <td style={tdStyle}>
                <Link to={`/runs/${run.id}`} style={{ color: theme.text, textDecoration: 'none', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {run.run_token}
                </Link>
              </td>
              <td style={tdStyle}>
                <span
                  title={run.quality_detail || ''}
                  style={{ color: statusColor(run.status), fontWeight: 500, fontSize: '0.85rem', cursor: run.quality_detail ? 'help' : 'default' }}
                >
                  {statusLabel(run.status)}{run.quality_detail && ['send_failed', 'receive_unreadable', 'manually_failed'].includes(run.status) ? ' ⓘ' : ''}
                </span>
              </td>
              <td style={{ ...tdStyle, fontSize: '0.8rem' }}>{fmtDateTime(run.t_submitted)}</td>
              <td style={tdStyle}>{run.e2e_duration_seconds ? `${run.e2e_duration_seconds.toFixed(1)}s` : '—'}</td>
              <td style={tdStyle}>
                {/* THE SAME CELL THE GLOBAL HISTORY USES, and it is shared for the reason the
                    SLA cell is: this was a hand-written copy of that page's quality cell,
                    carrying the same defect. It rendered `quality_result` — the OCR readability
                    enum — coloured green/amber/red under a column read as the quality SLA, so a
                    fax scoring 12 against a minimum of 70 showed a green `pass`. */}
                <QualityCell run={run} />
              </td>
              <td style={tdStyle}><SlaCell run={run} /></td>
              <td style={tdStyle}>
                <OutboundInfoButton transmissionId={run.id} onRefreshed={reload} />
                {!['in_flight', 'sending'].includes(run.status) && isAdmin && (
                  <button
                    onClick={() => handleDelete(run.id)}
                    title="Delete this run"
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: '#1e1e1e', color: '#ef5350', border: '1px solid #5a2a2a', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '0.4rem 0.8rem' }}>← Prev</button>
        <span style={{ padding: '0.4rem', fontSize: '0.9rem' }}>Page {page}</span>
        <button onClick={() => setPage(p => p + 1)} style={{ padding: '0.4rem 0.8rem' }}>Next →</button>
      </div>
    </div>
  )
}

const thStyle: React.CSSProperties = { padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#9aa0a6' }
const tdStyle: React.CSSProperties = { padding: '0.5rem 0.75rem' }

